import { SUPPORTED_INTERVIEW_LANGUAGES } from '@kiku/config/constants';
import { z } from 'zod';

// --- Tenant ---

export const tenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type Tenant = z.infer<typeof tenantSchema>;

export const createTenantInputSchema = z.object({
  name: z.string().min(1).max(200)
});

export type CreateTenantInput = z.infer<typeof createTenantInputSchema>;

export const updateTenantInputSchema = z.object({
  name: z.string().min(1).max(200)
});

export type UpdateTenantInput = z.infer<typeof updateTenantInputSchema>;

// --- Organization ---

export const organizationSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  workosOrgId: z.string().nullable(),
  language: z.enum(SUPPORTED_INTERVIEW_LANGUAGES),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type Organization = z.infer<typeof organizationSchema>;

const orgNameSlugFields = {
  name: z.string().min(1).max(200),
  slug: z
    .string()
    .min(1)
    .max(5)
    .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
};

export const createOrgInputSchema = z.discriminatedUnion('tenantMode', [
  z.object({
    tenantMode: z.literal('new'),
    tenantName: z.string().min(1).max(200),
    ...orgNameSlugFields
  }),
  z.object({
    tenantMode: z.literal('existing'),
    tenantId: z.string().uuid(),
    ...orgNameSlugFields
  })
]);

export type CreateOrgInput = z.infer<typeof createOrgInputSchema>;

export const updateOrgInputSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    slug: z
      .string()
      .min(1)
      .max(5)
      .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
      .optional(),
    language: z.enum(SUPPORTED_INTERVIEW_LANGUAGES).optional()
  })
  .refine(
    (data) => data.name !== undefined || data.slug !== undefined || data.language !== undefined,
    { message: 'At least one of name, slug, or language must be provided' }
  );

export type UpdateOrgInput = z.infer<typeof updateOrgInputSchema>;

// --- Department ---

export const departmentSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  orgId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type Department = z.infer<typeof departmentSchema>;

export const createDeptInputSchema = z.object({
  orgId: z.string().uuid(),
  name: z.string().min(1).max(200),
  slug: z
    .string()
    .min(1)
    .max(30)
    .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
});

export type CreateDeptInput = z.infer<typeof createDeptInputSchema>;

export const updateDeptInputSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    slug: z
      .string()
      .min(1)
      .max(30)
      .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
      .optional()
  })
  .refine((data) => data.name !== undefined || data.slug !== undefined, {
    message: 'At least one of name or slug must be provided'
  });

export type UpdateDeptInput = z.infer<typeof updateDeptInputSchema>;

// --- Errors ---

export class AdminNotFoundError extends Error {
  constructor(resource: string) {
    super(`${resource} not found`);
    this.name = 'AdminNotFoundError';
  }
}

export class AdminConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AdminConflictError';
  }
}
